import {FirebaseUser, IdTokenResult, SsoProvider, User} from "./types";
import {UserInfo} from "@firebase/auth"
import {hashSHA256} from "../../accounts-api/accounts-api";
import {AuthProviderType} from "../../domain/providers";

export const toDomainUser = async (firebaseUser: FirebaseUser): Promise<User> => {
  const idTokenResult = await firebaseUser.getIdTokenResult(true)
  return toWakeletUser(firebaseUser, idTokenResult)
};

export const toWakeletUser = (firebaseUser: FirebaseUser, idTokenResult: IdTokenResult = {
  claims: {},
  token: ""
}): User => {
  const email = firebaseUser.email || undefined;
  const displayName = firebaseUser.displayName || undefined;
  const picture = firebaseUser.photoURL || undefined;
  const provider = getProvider(firebaseUser.providerData);
  const contentId = idTokenResult.claims["contentId"];
  const emailVerified = firebaseUser.emailVerified;
  const providers = firebaseUser.providerData.map(p => ({provider: p.providerId, uid: p.uid}));
  const creationTime = parseDateOrUndefined(firebaseUser.metadata.creationTime);
  return {
    uid: firebaseUser.uid,
    provider,
    providers,
    email,
    displayName,
    photoUrl: picture,
    contentId,
    emailVerified,
    getIdToken: firebaseUser.getIdToken.bind(firebaseUser),
    creationTime,
  }
};

const getProvider = (providerData: UserInfo[]): AuthProviderType => {
  if (!providerData || providerData.length === 0) {
    console.warn("No provider data found");
    return "unknown" as AuthProviderType;
  }
  return providerData[0].providerId as AuthProviderType
}

function parseDateOrUndefined(value?: string | null): Date | undefined {
    if (!value) return undefined;
    const ts = Date.parse(value);
    return Number.isNaN(ts) ? undefined : new Date(ts);
}

export const hashAccount = (user: User): string => {
  const accountToHash = toAccountHash(user);
  const value = JSON.stringify(accountToHash);
  return hashSHA256(value);
};

function toAccountHash(user: User) {
  const accountToHash: AccountHash = {
    uid: user.uid,
    email: user.email || "",
    contentId: user.contentId || "",
    emailVerified: user.emailVerified || false,
    ssoIdentities: toHashProviders(user.providers)
  }
  return accountToHash;
}

const toHashProviders = (providers?: SsoProvider[]): SsoProviderHash[] | undefined => {
  if (!providers) {
    return undefined
  }
  const hashProviders = providers.map(p => ({provider: p.provider, uid: p.uid}));
  hashProviders.sort((left, right) => {
    if (left.provider > right.provider) {
      return 1
    }
    if (left.provider < right.provider) {
      return -1;
    }
    return 0
  })
  return hashProviders
}

type AccountHash = {
  uid: string
  email: string
  contentId: string
  emailVerified: boolean
  ssoIdentities?: SsoProviderHash[],
}

type SsoProviderHash = {
  provider: string,
  uid: string,
}
